/**
 * A small Markdown reader for the assistant's replies (assistant panel, part 2). It turns the model's Markdown into a
 * tree of plain data (headings, paragraphs, bold and italic, lists, tables, links, inline and block code, quotes and
 * rules) that components/assistant/Markdown.tsx draws as React elements. Nothing here produces HTML: raw HTML in a
 * reply stays as text, and React escapes all text, so a reply can never inject markup or script.
 *
 * Links are kept only when safeHref() allows them: a portal path (/projects/12), or an http, https, mailto or tel
 * address. Anything else (javascript:, data:, vbscript:, file:, a protocol-relative //host) is shown as its text with
 * no link. It has no React import, so the API tests check it too (apps/api/test/assistant-markdown.test.ts).
 */

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'strong'; c: Inline[] }
  | { t: 'em'; c: Inline[] }
  | { t: 'del'; c: Inline[] }
  | { t: 'code'; v: string }
  | { t: 'link'; href: string; internal: boolean; c: Inline[] }
  | { t: 'br' };
export type ListItem = { c: Inline[]; sub: Block[] };
export type Align = 'left' | 'center' | 'right' | null;
export type Block =
  | { t: 'h'; level: number; c: Inline[] }
  | { t: 'p'; c: Inline[] }
  | { t: 'ul'; items: ListItem[] }
  | { t: 'ol'; start: number; items: ListItem[] }
  | { t: 'code'; lang: string; v: string }
  | { t: 'quote'; c: Block[] }
  | { t: 'table'; head: Inline[][]; align: Align[]; rows: Inline[][][] }
  | { t: 'hr' };

const MAX_SOURCE = 100_000;
const MAX_DEPTH = 4;

/**
 * The link a reply may carry, or null. A portal path stays inside the portal; an http(s), mailto or tel address opens
 * outside it. Everything else is refused.
 */
export function safeHref(raw: string): { href: string; internal: boolean } | null {
  const href = raw.trim();
  if (!href || href.length > 2000) return null;
  // Control characters and spaces can hide a scheme from a simple check, so a link with any is refused.
  if (/[\u0000- \u007f-\u009f\\]/.test(href)) return null;
  if (href.startsWith('/')) return href.startsWith('//') ? null : { href, internal: true };
  if (href.startsWith('#')) return null;
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(href)?.[1]?.toLowerCase();
  if (scheme === 'http' || scheme === 'https') {
    try {
      const u = new URL(href);
      return u.hostname ? { href: u.href, internal: false } : null;
    } catch {
      return null;
    }
  }
  if (scheme === 'mailto' || scheme === 'tel') return { href, internal: false };
  return null;
}

/* ------------------------------------------------------------------ inline */

const PUNCT = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/;
const URL_RE = /https?:\/\/[^\s<>"'`]+/y;

function pushText(out: Inline[], v: string) {
  if (!v) return;
  const last = out[out.length - 1];
  if (last && last.t === 'text') last.v += v;
  else out.push({ t: 'text', v });
}

/** The index of the closing delimiter for emphasis, or -1. The closer must follow non-space text. */
function findCloser(s: string, from: number, delim: string) {
  let i = from;
  while (i < s.length) {
    const ch = s[i]!;
    if (ch === '\\') {
      i += 2;
      continue;
    }
    if (ch === '`') {
      const run = /^`+/.exec(s.slice(i))![0];
      const end = s.indexOf(run, i + run.length);
      if (end < 0) {
        i += run.length;
        continue;
      }
      i = end + run.length;
      continue;
    }
    if (s.startsWith(delim, i) && i > from && !/\s/.test(s[i - 1]!)) {
      // "**" is not the closer of "*"
      if (delim.length === 1 && s[i + 1] === delim) {
        i += 2;
        continue;
      }
      if (delim === '_' && i + 1 < s.length && /\w/.test(s[i + 1]!)) {
        i++;
        continue;
      }
      return i;
    }
    i++;
  }
  return -1;
}

/** Finds the ] that closes a link label starting after `[`, skipping code spans and escapes. */
function findLabelEnd(s: string, from: number) {
  let depth = 0;
  for (let i = from; i < s.length; i++) {
    const ch = s[i]!;
    if (ch === '\\') {
      i++;
      continue;
    }
    if (ch === '[') depth++;
    else if (ch === ']') {
      if (!depth) return i;
      depth--;
    }
  }
  return -1;
}

export function parseInline(s: string, depth = 0): Inline[] {
  const out: Inline[] = [];
  let i = 0;
  while (i < s.length) {
    const ch = s[i]!;
    // \ before punctuation keeps it literal; \ at the end of a line is a line break
    if (ch === '\\' && i + 1 < s.length) {
      const next = s[i + 1]!;
      if (next === '\n') {
        out.push({ t: 'br' });
        i += 2;
        continue;
      }
      if (PUNCT.test(next)) {
        pushText(out, next);
        i += 2;
        continue;
      }
    }
    if (ch === '\n') {
      out.push({ t: 'br' });
      i++;
      continue;
    }
    if (ch === '`') {
      const run = /^`+/.exec(s.slice(i))![0];
      const end = s.indexOf(run, i + run.length);
      if (end >= 0) {
        let v = s.slice(i + run.length, end).replace(/\n/g, ' ');
        if (v.length > 2 && v.startsWith(' ') && v.endsWith(' ')) v = v.slice(1, -1);
        out.push({ t: 'code', v });
        i = end + run.length;
        continue;
      }
      pushText(out, run);
      i += run.length;
      continue;
    }
    if (depth < MAX_DEPTH && (ch === '*' || ch === '_' || ch === '~')) {
      const two = s.startsWith(ch.repeat(2), i);
      if (ch === '~' && !two) {
        pushText(out, ch);
        i++;
        continue;
      }
      // _ inside a word, as in file_name, is just a character
      if (ch === '_' && i > 0 && /\w/.test(s[i - 1]!)) {
        pushText(out, ch);
        i++;
        continue;
      }
      const delim = two ? ch.repeat(2) : ch;
      const start = i + delim.length;
      if (start < s.length && !/\s/.test(s[start]!)) {
        const end = findCloser(s, start, delim);
        if (end > start) {
          const c = parseInline(s.slice(start, end), depth + 1);
          out.push(ch === '~' ? { t: 'del', c } : two ? { t: 'strong', c } : { t: 'em', c });
          i = end + delim.length;
          continue;
        }
      }
      pushText(out, delim);
      i += delim.length;
      continue;
    }
    if (ch === '[' && depth < MAX_DEPTH) {
      const close = findLabelEnd(s, i + 1);
      if (close > i && s[close + 1] === '(') {
        // the address runs to the matching ), and may end with a "title", which is dropped
        let j = close + 2;
        let paren = 0;
        while (j < s.length) {
          if (s[j] === '\\') j++;
          else if (s[j] === '(') paren++;
          else if (s[j] === ')') {
            if (!paren) break;
            paren--;
          } else if (s[j] === '\n') break;
          j++;
        }
        if (s[j] === ')') {
          const inner = s.slice(close + 2, j).trim();
          const target = /^<([^>]*)>/.exec(inner)?.[1] ?? inner.split(/\s+/)[0] ?? '';
          const label = parseInline(s.slice(i + 1, close), depth + 1);
          const safe = safeHref(target);
          if (safe) out.push({ t: 'link', href: safe.href, internal: safe.internal, c: label.length ? label : [{ t: 'text', v: safe.href }] });
          else for (const l of label) l.t === 'text' ? pushText(out, l.v) : out.push(l);
          i = j + 1;
          continue;
        }
      }
    }
    if (ch === '<') {
      const m = /^<((?:https?:\/\/|mailto:)[^\s<>]+)>/i.exec(s.slice(i));
      const safe = m ? safeHref(m[1]!) : null;
      if (m && safe) {
        out.push({ t: 'link', href: safe.href, internal: false, c: [{ t: 'text', v: m[1]!.replace(/^mailto:/i, '') }] });
        i += m[0].length;
        continue;
      }
    }
    if ((ch === 'h' || ch === 'H') && (i === 0 || !/\w/.test(s[i - 1]!))) {
      URL_RE.lastIndex = i;
      const m = URL_RE.exec(s);
      if (m) {
        // trailing punctuation belongs to the sentence, not the address
        const url = m[0].replace(/[.,;:!?)\]]+$/, '');
        const safe = safeHref(url);
        if (safe) {
          out.push({ t: 'link', href: safe.href, internal: false, c: [{ t: 'text', v: url }] });
          i += url.length;
          continue;
        }
      }
    }
    pushText(out, ch);
    i++;
  }
  return out;
}

/* ------------------------------------------------------------------ blocks */

const FENCE = /^ {0,3}(`{3,}|~{3,})\s*([\w+#.-]*)[^`]*$/;
const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/;
const RULE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const QUOTE = /^ {0,3}>[ ]?(.*)$/;
const ITEM = /^( *)([-*+]|\d{1,9}[.)])[ \t]+(.*)$/;
const DELIM = /^ *\|? *:?-+:? *(\| *:?-+:? *)*\|? *$/;

/** The cells of a table row, split on | outside code spans, with an escaped \| kept as a character. */
function cells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);
  const out: string[] = [];
  let cur = '';
  let code = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (ch === '\\' && s[i + 1] === '|') {
      cur += '|';
      i++;
    } else if (ch === '`') {
      code = !code;
      cur += ch;
    } else if (ch === '|' && !code) {
      out.push(cur.trim());
      cur = '';
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

const startsBlock = (line: string) => FENCE.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line) || ITEM.test(line);

function parseBlocks(lines: string[], depth: number): Block[] {
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    const fence = FENCE.exec(line);
    if (fence) {
      const mark = fence[1]!;
      const body: string[] = [];
      i++;
      while (i < lines.length && !new RegExp(`^ {0,3}${mark[0] === '`' ? '`' : '~'}{${mark.length},}\\s*$`).test(lines[i]!)) body.push(lines[i++]!);
      i++;
      out.push({ t: 'code', lang: fence[2] ?? '', v: body.join('\n') });
      continue;
    }
    const h = HEADING.exec(line);
    if (h) {
      out.push({ t: 'h', level: h[1]!.length, c: parseInline(h[2] ?? '') });
      i++;
      continue;
    }
    if (RULE.test(line)) {
      out.push({ t: 'hr' });
      i++;
      continue;
    }
    if (QUOTE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i]!)) body.push(QUOTE.exec(lines[i++]!)![1]!);
      out.push(depth < MAX_DEPTH ? { t: 'quote', c: parseBlocks(body, depth + 1) } : { t: 'p', c: parseInline(body.join('\n')) });
      continue;
    }
    const item = ITEM.exec(line);
    if (item) {
      const indent = item[1]!.length;
      const ordered = /\d/.test(item[2]!);
      const items: ListItem[] = [];
      while (i < lines.length) {
        const m = ITEM.exec(lines[i]!);
        if (!m || m[1]!.length !== indent || /\d/.test(m[2]!) !== ordered) break;
        const text = [m[3]!];
        const sub: string[] = [];
        i++;
        // the item goes on over indented lines and lazy continuation lines; deeper items make a nested list
        while (i < lines.length) {
          const next = lines[i]!;
          if (!next.trim()) {
            const after = lines[i + 1];
            const afterLead = after ? after.length - after.trimStart().length : 0;
            // more of this item after a blank line, indented under it
            if (after && after.trim() && afterLead > indent && !(ITEM.exec(after) && afterLead <= indent)) {
              sub.push('');
              i++;
              continue;
            }
            // a blank line between two items of the same list
            if (after && ITEM.exec(after)?.[1]!.length === indent) i++;
            break;
          }
          const lead = next.length - next.trimStart().length;
          const nested = ITEM.exec(next);
          if (nested && nested[1]!.length <= indent) break;
          if (lead > indent && (nested || sub.length)) {
            sub.push(next.slice(Math.min(lead, indent + 2)));
            i++;
            continue;
          }
          if (startsBlock(next) && lead <= indent) break;
          text.push(next.trim());
          i++;
        }
        items.push({ c: parseInline(text.join('\n')), sub: sub.length && depth < MAX_DEPTH ? parseBlocks(sub, depth + 1) : [] });
      }
      out.push(ordered ? { t: 'ol', start: Number(/\d+/.exec(item[2]!)![0]), items } : { t: 'ul', items });
      continue;
    }
    if (line.includes('|') && i + 1 < lines.length && DELIM.test(lines[i + 1]!) && lines[i + 1]!.includes('-')) {
      const head = cells(line);
      const align: Align[] = cells(lines[i + 1]!).map((c) => (c.startsWith(':') && c.endsWith(':') ? 'center' : c.endsWith(':') ? 'right' : c.startsWith(':') ? 'left' : null));
      const rows: Inline[][][] = [];
      i += 2;
      while (i < lines.length && lines[i]!.trim() && lines[i]!.includes('|')) {
        const r = cells(lines[i++]!);
        rows.push(head.map((_, k) => parseInline(r[k] ?? '')));
      }
      out.push({ t: 'table', head: head.map((c) => parseInline(c)), align: head.map((_, k) => align[k] ?? null), rows });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !(para.length && startsBlock(lines[i]!)) && !(lines[i]!.includes('|') && i + 1 < lines.length && DELIM.test(lines[i + 1]!) && lines[i + 1]!.includes('-'))) {
      para.push(lines[i]!.trim());
      i++;
    }
    if (!para.length) {
      // a line that looks like a block start but did not make one, such as a lone "|"
      para.push(lines[i]!.trim());
      i++;
    }
    out.push({ t: 'p', c: parseInline(para.join('\n')) });
  }
  return out;
}

export function parseMarkdown(src: string): Block[] {
  const text = (src ?? '').slice(0, MAX_SOURCE).replace(/\r\n?/g, '\n').replace(/\t/g, '    ');
  return parseBlocks(text.split('\n'), 0);
}

/* ------------------------------------------------------------------ plain text, for Copy */

function inlineText(c: Inline[], origin: string): string {
  return c
    .map((n) => {
      if (n.t === 'text' || n.t === 'code') return n.v;
      if (n.t === 'br') return '\n';
      if (n.t === 'link') {
        const label = inlineText(n.c, origin);
        const href = n.internal ? `${origin}${n.href}` : n.href.replace(/^mailto:/i, '');
        return label === href || label === n.href ? href : `${label} (${href})`;
      }
      return inlineText(n.c, origin);
    })
    .join('');
}

function blocksText(blocks: Block[], origin: string, indent = ''): string {
  return blocks
    .map((b) => {
      switch (b.t) {
        case 'h':
        case 'p':
          return indent + inlineText(b.c, origin).replace(/\n/g, `\n${indent}`);
        case 'code':
          return b.v;
        case 'hr':
          return '';
        case 'quote':
          return blocksText(b.c, origin, indent);
        case 'table':
          return [b.head, ...b.rows].map((r) => indent + r.map((c) => inlineText(c, origin)).join('\t')).join('\n');
        case 'ul':
        case 'ol':
          return b.items
            .map((it, k) => {
              const mark = b.t === 'ol' ? `${b.start + k}. ` : '- ';
              const sub = it.sub.length ? `\n${blocksText(it.sub, origin, `${indent}  `)}` : '';
              return `${indent}${mark}${inlineText(it.c, origin).replace(/\n/g, `\n${indent}  `)}${sub}`;
            })
            .join('\n');
      }
    })
    .join('\n\n');
}

/** A reply as plain text, for the Copy button: no Markdown marks, and each link written out with its address. */
export function markdownToText(src: string, origin = ''): string {
  return blocksText(parseMarkdown(src), origin).replace(/\n{3,}/g, '\n\n').trim();
}
